import {
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Instant } from "@js-joda/core";
import { useState } from "react";
import reactBridge from "../../../../core/react-bridge";
import OpenForResubmissionModal from "./OpenForResubmissionModal";
import {
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientId,
  PatientTaskInstanceId,
} from "../../../../shared/schema/schema";
import {
  useGenerateDocumentPdfUrl,
  useMarkDocumentApproved,
  useMarkDocumentSubmitted,
  useReopenDocumentForSubmission,
} from "../actions/documentActions";
import { deriveDocStatus } from "./docStatus";

type DocRow = {
  documentTypeId: PatientDocumentTypeId;
  title: string;
  fileUrl: string | null;
  submitted: boolean;
  approved: boolean;
  approvedByName: string | null;
  isScanned: boolean;
  sentToPhysician: boolean;
  isOpenForResubmission: boolean;
  documentScheduleId: PatientDocumentScheduleId | null;
  reopenHistory: {
    nodes: { reason: string; reopenedAt: Instant; reopenedByName: string | null }[];
  };
};

interface Props {
  taskInstanceId: PatientTaskInstanceId;
  patientId: PatientId;
  patientName: string;
  caregiverName: string | null;
  documents: DocRow[];
}

export default function TasksNewDocumentsCell(props: Props) {
  const { taskInstanceId, patientId, patientName, caregiverName, documents } = props;
  const toast = useToast();

  const resubmissionDisclosure = useDisclosure();
  const [resubmissionDocId, setResubmissionDocId] = useState<PatientDocumentScheduleId | null>(
    null
  );

  const reopenMutation = useReopenDocumentForSubmission();
  const approveMutation = useMarkDocumentApproved();
  const submitMutation = useMarkDocumentSubmitted();
  const generatePdfUrlMutation = useGenerateDocumentPdfUrl();

  async function openSignedPdf(documentScheduleId: PatientDocumentScheduleId) {
    // Open a blank tab synchronously to avoid Safari/Chrome popup blocking;
    // we fill in its location once the signed URL resolves.
    const win = window.open("about:blank", "_blank");
    try {
      const fileUrl = await generatePdfUrlMutation.mutateAsync({
        documentScheduleId,
        patientId,
      });
      if (win) {
        win.location.href = fileUrl;
      } else {
        window.open(fileUrl, "_blank");
      }
    } catch {
      if (win) win.close();
      toast({
        title: "Failed to view document PDF",
        description: "Something went wrong while generating the document link.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }

  function handleConfirmResubmission(reason: string) {
    if (resubmissionDocId === null) {
      return;
    }

    reopenMutation.mutate(
      { documentScheduleId: resubmissionDocId, reason },
      {
        onSuccess: () => {
          setResubmissionDocId(null);
          resubmissionDisclosure.onClose();
        },
        onError: () => {
          toast({
            title: "Something went wrong",
            description: "Could not reopen for submission",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        },
      }
    );
  }

  if (documents.length === 0) {
    return <>—</>;
  }

  return (
    <>
      <HStack flexWrap="wrap" spacing={1}>
        {documents.map((doc) => {
          const status = deriveDocStatus(doc);
          const reopenEntries = doc.reopenHistory.nodes;
          const approvedByName = doc.approved ? doc.approvedByName : null;

          // View PDF is available for any submitted document that has a
          // scheduled-document id (the id is what the server uses to look up
          // the file and generate a signed S3 URL). We don't gate on fileUrl
          // — that column holds an S3 key, not a usable URL, and some
          // submitted documents have it null even when a file exists.
          const showViewPdf = doc.submitted && doc.documentScheduleId !== null;

          const showOpenForSubmission = doc.submitted;

          const showReviewForm = (doc.submitted && !doc.isScanned) || !doc.submitted;

          const showMarkApproved = doc.submitted && !doc.approved;

          const showMarkSubmitted = !doc.submitted;

          return (
            <Menu key={String(doc.documentTypeId)} isLazy>
              <Tooltip
                hasArrow
                isDisabled={reopenEntries.length === 0 && approvedByName === null}
                label={
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold">{status.label}</Text>
                    {approvedByName !== null && <Text>Approved by {approvedByName}</Text>}
                    {reopenEntries.map((entry, i) => (
                      <Text key={i}>
                        {entry.reopenedAt.toString().slice(0, 10)} ·{" "}
                        {entry.reopenedByName ?? "Unknown"}: {entry.reason}
                      </Text>
                    ))}
                  </VStack>
                }
                placement="top"
              >
                <MenuButton
                  as={Tag}
                  colorScheme={status.colorScheme}
                  cursor="pointer"
                  size="sm"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <TagLabel>{doc.title}</TagLabel>
                </MenuButton>
              </Tooltip>
              <MenuList onClick={(e) => e.stopPropagation()}>
                {showViewPdf && (
                  <MenuItem
                    isDisabled={generatePdfUrlMutation.isPending}
                    onClick={() => {
                      if (doc.documentScheduleId !== null) {
                        void openSignedPdf(doc.documentScheduleId);
                      }
                    }}
                  >
                    View PDF
                  </MenuItem>
                )}
                {showViewPdf &&
                  (showOpenForSubmission ||
                    showReviewForm ||
                    showMarkApproved ||
                    showMarkSubmitted) && <MenuDivider />}
                {showOpenForSubmission && (
                  <MenuItem
                    isDisabled={reopenMutation.isPending}
                    onClick={() => {
                      if (doc.documentScheduleId !== null) {
                        setResubmissionDocId(doc.documentScheduleId);
                        resubmissionDisclosure.onOpen();
                      }
                    }}
                  >
                    Open for submission
                  </MenuItem>
                )}
                {showReviewForm && (
                  <MenuItem
                    onClick={() => {
                      reactBridge.dispatch("OPEN_PATIENT_DOCUMENT_REVIEW_MODAL", {
                        taskInstanceId,
                        documentTypeId: doc.documentTypeId,
                        patientId,
                        patientName,
                        caregiverName: caregiverName ?? null,
                      });
                    }}
                  >
                    Review form
                  </MenuItem>
                )}
                {showMarkApproved && (
                  <MenuItem
                    isDisabled={approveMutation.isPending}
                    onClick={() => {
                      if (doc.documentScheduleId !== null) {
                        approveMutation.mutate({
                          documentScheduleId: doc.documentScheduleId,
                        });
                      }
                    }}
                  >
                    Mark as approved
                  </MenuItem>
                )}
                {showMarkSubmitted && (
                  <MenuItem
                    isDisabled={submitMutation.isPending}
                    onClick={() => {
                      submitMutation.mutate({
                        taskInstanceId,
                        documentTypeId: doc.documentTypeId,
                      });
                    }}
                  >
                    Mark as submitted
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          );
        })}
      </HStack>
      <OpenForResubmissionModal
        disclosure={resubmissionDisclosure}
        isLoading={reopenMutation.isPending}
        onConfirm={handleConfirmResubmission}
      />
    </>
  );
}
